// The ONE way an admin write leaves this SPA (#5724): through a short-lived
// operator credential from `POST /v1/admin/operator-tokens`, never on the
// cookie session. The enumeration behind "the ONE way": every write plane in
// the `PLANES` table of `operator-planes.test.ts`, grants in
// `grant-actions.test.ts`, and `scripts/admin-session-writes.test.ts`, the guard
// that turns the next admin write sent on the session red.
//
// WHY. A staff cookie carries the radar (`admin:read:*`) and no admin write
// scope, by design: `apps/api/src/routes/operator-tokens.ts` explains why a
// long-lived, ambient cookie must not be able to act on a tenant. So every
// staff write (approve a user, set a tier, issue a grant, …) first asks that
// door for a one-hour credential carrying EXACTLY the scopes the act needs,
// then sends the write with `Authorization: Bearer <credential>`.
//
// HELD IN MEMORY ONLY. The plaintext lives in this module's `held` list and
// nowhere else: never `localStorage` / `sessionStorage`, never a URL, never a
// console line. A reload or a sign-out (which reloads) drops it. It is reused
// for any later act whose scopes it already covers until shortly before its
// `expires_iso` — every issuance is an audited token row, so minting one per
// click would bury the trail.
//
// THE DOOR'S TWO REFUSALS STAY APART, as a discriminated outcome, and on
// either NO write is sent:
//   - `not_staff`: a plain 403 `forbidden` — the session lacks
//     `admin:read:platform` (or is a PAT, which may not issue a credential).
//   - `reauth_required`: 403 `https://developerz.ai/errors/reauth-required`
//     (`ERROR_TYPES.reauthRequired`): staff, but signed in over an hour ago
//     (`OPERATOR_STEP_UP_MAX_AGE_MS`). The fix is a fresh sign-in. `ApiError`
//     carries the problem's extension members, not its `type`, so this reads
//     the `reason: 'reauth_required'` member that type documents.
// Any OTHER failure (a 5xx, a dead network, the write's own refusal) throws as
// it always did, so the callers' existing error copy still applies.

import type { Scope } from '@developerz/domain';
import { issueOperatorToken } from './adminGrants';
import { apiFetch } from './api';
import { ApiError, isUnauthorized } from './api-errors';

/** The scopes a staff write may ask the door for. */
export type OperatorScope = Extract<
  Scope,
  // `admin:releases`: the Fleet dialog's runner promote request (#6735).
  'admin:users' | 'admin:accounts' | 'admin:grants' | 'admin:goals' | 'admin:releases'
>;

/** Why the door refused — each has its own remedy, so they are never collapsed. */
export type OperatorRefusal = 'not_staff' | 'reauth_required';

/** A write's outcome: it ran (`value` is its answer), or the door refused and nothing was sent. */
export type OperatorOutcome<T> =
  | { readonly kind: 'ok'; readonly value: T }
  | { readonly kind: OperatorRefusal };

/**
 * How a write is sent on the credential: `apiFetch` with the credential as the
 * bearer and `credential: 'operator'`, so its 401 never signs the session out.
 * Handed to the write rather than the raw token, so no write can forget that.
 */
export type OperatorSend = <R>(path: string, init?: RequestInit) => Promise<R>;

/** What one act asks the door for. `name` lands on the audited token row. */
export type OperatorAsk = {
  readonly scopes: readonly OperatorScope[];
  readonly name: string;
  /** The SESSION credential the door is asked with (`SESSION_TOKEN`). */
  readonly token: string | null;
};

type Issued =
  | { readonly kind: 'ok'; readonly token: string; readonly expiresAt: number }
  | { readonly kind: OperatorRefusal };
type Entry = {
  readonly scopes: readonly OperatorScope[];
  readonly minted: Promise<Issued>;
  /** Null while the issuance is in flight — an in-flight entry is shared, not re-asked. */
  expiresAt: number | null;
  /** The issued plaintext once it lands, so a 401 on it can drop exactly this entry. */
  token: string | null;
};

/**
 * Retire a credential this long before its `expires_iso`. A write that lands on
 * an expired bearer is a 401, and `apiFetch` reads every 401 as the SESSION
 * dying — it would sign the operator out over a credential that merely aged.
 */
const EXPIRY_MARGIN_MS = 60_000;

let held: Entry[] = [];

async function askDoor(ask: OperatorAsk): Promise<Issued> {
  try {
    const issued = await issueOperatorToken(ask.scopes, ask.name, ask.token);
    return { kind: 'ok', token: issued.token, expiresAt: Date.parse(issued.expires_iso) };
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 403) throw err;
    return {
      kind: err.extensions?.['reason'] === 'reauth_required' ? 'reauth_required' : 'not_staff',
    };
  }
}

/** A held credential covering `ask`, or a fresh issuance (shared while in flight). */
function credentialFor(ask: OperatorAsk): Promise<Issued> {
  const now = Date.now();
  held = held.filter((e) => e.expiresAt === null || e.expiresAt - EXPIRY_MARGIN_MS > now);
  const hit = held.find((e) => ask.scopes.every((s) => e.scopes.includes(s)));
  if (hit !== undefined) return hit.minted;
  const entry: Entry = { scopes: ask.scopes, minted: askDoor(ask), expiresAt: null, token: null };
  held.push(entry);
  const drop = (): void => {
    held = held.filter((e) => e !== entry);
  };
  // A refusal or a fault is never held: the next click asks again.
  entry.minted.then((m) => {
    if (m.kind !== 'ok') return drop();
    entry.expiresAt = m.expiresAt;
    entry.token = m.token;
  }, drop);
  return entry.minted;
}

const sendOn =
  (credential: string): OperatorSend =>
  (path, init) =>
    apiFetch(path, credential, init, { credential: 'operator' });

/** Stop holding `token` — the server said it is gone. Idempotent across concurrent writers. */
function forget(token: string): void {
  held = held.filter((e) => e.token !== token);
}

/** The write on one credential; a 401 forgets that credential, then rethrows. */
async function writeOn<T>(token: string, write: (send: OperatorSend) => Promise<T>): Promise<T> {
  try {
    return await write(sendOn(token));
  } catch (err) {
    if (isUnauthorized(err)) forget(token);
    throw err;
  }
}

/**
 * Run one staff write under an operator credential covering `ask.scopes`.
 * `write` gets an {@link OperatorSend} bound to the credential and is called
 * ONLY when the door issued one.
 *
 * A 401 on the write is the CREDENTIAL's (revoked mid-life, or a clock running
 * fast past {@link EXPIRY_MARGIN_MS}) — `credential: 'operator'` keeps it off the
 * session latch. It is forgotten and the write retried ONCE on a fresh one; a
 * second 401 is thrown as the write's own error. Never a loop, never a sign-out.
 *
 * The DOOR's 401 is not retried: the door is asked with the session, so that 401
 * is the session's — `apiFetch` has already signed the app out, and it throws.
 */
export async function withOperatorCredential<T>(
  ask: OperatorAsk,
  write: (send: OperatorSend) => Promise<T>,
): Promise<OperatorOutcome<T>> {
  const first = await credentialFor(ask);
  if (first.kind !== 'ok') return first;
  try {
    return { kind: 'ok', value: await writeOn(first.token, write) };
  } catch (err) {
    if (!isUnauthorized(err)) throw err;
  }
  const fresh = await credentialFor(ask);
  if (fresh.kind !== 'ok') return fresh;
  return { kind: 'ok', value: await writeOn(fresh.token, write) };
}

/** Drop every held credential. Tests call it between cases; a sign-out reloads instead. */
export function forgetOperatorCredentials(): void {
  held = [];
}
